import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Upload, X, AlertTriangle } from 'lucide-react';
import { storageService } from '../../../services/storageService';

/**
 * 画像アップロード用コンポーネント
 */
const ImageUpload = ({ 
  onImageUploaded, 
  onImageRemoved, 
  currentImage = null,
  disabled = false,
  className = '',
  currentUser = null
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file || disabled) return;

    setError(null);

    // ファイルのバリデーション
    const validation = storageService.validateImage(file);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      return;
    }

    setIsUploading(true);

    try {
      const result = await storageService.uploadImage(file, 'quiz-images', currentUser);
      onImageUploaded(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    handleFileSelect(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveImage = async () => {
    if (!currentImage || disabled) return;

    try {
      if (currentImage.fullPath) {
        await storageService.deleteImage(currentImage.fullPath, currentUser);
      }
      onImageRemoved();
    } catch (err) {
      setError('画像の削除に失敗しました');
    }
  };

  const openFileDialog = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />

      {currentImage ? (
        // 既存画像の表示
        <div className="relative">
          <img
            src={currentImage.url}
            alt="アップロード済み画像"
            className="w-full max-w-md h-auto rounded-lg border border-gray-300"
            style={{ maxHeight: '300px', objectFit: 'contain' }}
          />
          {!disabled && (
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              aria-label="画像を削除"
            >
              <X size={16} />
            </button>
          )}
          <div className="mt-2 text-xs text-gray-500">
            {currentImage.filename}
          </div>
        </div>
      ) : (
        // アップロード領域
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging 
              ? 'border-purple-400 bg-purple-50' 
              : 'border-gray-300 hover:border-purple-300 hover:bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFileDialog}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
              <div className="text-gray-600">アップロード中...</div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <div className="text-gray-600 mb-1">
                クリックまたはドラッグ&ドロップで画像をアップロード
              </div>
              <div className="text-xs text-gray-400">
                JPEG, PNG, GIF, WebP (最大5MB)
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-start">
          <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
          <div className="whitespace-pre-line">{error}</div>
        </div>
      )}
    </div>
  );
};

ImageUpload.propTypes = {
  onImageUploaded: PropTypes.func.isRequired,
  onImageRemoved: PropTypes.func.isRequired,
  currentImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
    filename: PropTypes.string,
    fullPath: PropTypes.string
  }),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  currentUser: PropTypes.object
};

export default ImageUpload; 